import React, { useState } from 'react';
import clsx from 'clsx';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import DatePicker from "react-datepicker";
import axios from 'axios';
import dayjs from 'dayjs'
import ja from "date-fns/locale/ja";
import { useCookies } from 'react-cookie';
import Cookies from 'universal-cookie'
import { Button } from 'src/components';
import { IconSearch, IconChevronDown } from 'src/icons';
import { useDrop, useVermins } from 'src/stores';
import { Checkbox2, Radio, RadiosWrap} from 'src/components';
import { picselBox } from 'src/images';
import styles from './SearchForm.module.scss';

type Props = {
  className?: string;
};

export type FormValues = {
  keyword: string;
  start_day: string;
  end_day: string;
  start_time: string;
  end_time: string;
  time_zone: string[];
  proc:string;
};

const SearchForm: React.FC<Props> = ({ className }) => {

  /**
   * 詳細検索バーの開閉状態管理ストアのプロパティとメソッド
   * ┬ isDroped: boolean
   * ├ true 開く
   * └ false 閉じる
   * ┬ dropToggle: () => void
   * └ isDroped の真偽値を反転する
   */
  const [isDroped, dropToggle] = useDrop(state => [state.isDroped, state.dropToggle]);

  /**
   * 検索結果管理ストアのセッター
   */
  const [setVermins, deleteVermins, setProc, setIds, deleteIds, searchS, searchE] = useVermins(state => [state.setVermins, state.deleteVermins, state.setProc, state.setIds, state.deleteIds, state.searchS, state.searchE]);

  /**
   * 時間帯選択を変更するフラグ
   */
  const [activeTab, setActiveTab] = useState(false);

  const [cookie] = useCookies();

  const cookies = new Cookies();

  /**
   * フォームを管理するカスタムフック
   * register を適用したフィールドの値を handleSubmit で取得できる
   * See https://react-hook-form.com/api/useform
   * @example
   * <Form onSubmit={handleSubmit(submit)}>
   *   <input {...register('field_name')} />
   *   ...
   * </Form>
   */
  const { handleSubmit, register, reset, watch, control } = useForm<FormValues>();

  /**
   * submitイベントのコールバック関数、詳細検索
   * @example
   * handleSubmit(search)
   */
  const search: SubmitHandler<FormValues> = data => {
    const start_date = data.start_day && dayjs(data.start_day).format("YYYY/MM/DD")
    const end_date = data.end_day && dayjs(data.end_day).format('YYYY/MM/DD')
    const start_time = data.start_time && dayjs(data.start_time).format('HH:mm')
    const end_time = data.end_time && dayjs(data.end_time).format('HH:mm')
    const word = data.keyword.trim().split(/\s+/).join();
    const proc = data.proc;
    if (!cookie?.verminData) {
      axios.get(`${process.env.REACT_APP_DEV_API_URL}/vermins/search`, {
        params: {
          word,
          start_date,
          end_date,
          start_time,
          end_time,
          time_zone: data.time_zone?.join(''),
          proc,
        }
      },
      ).then((data) => {
        setVermins(data.data.result);
        setProc(proc);
        setIds(data.data.ids);
        searchS(watch());
      }).catch(async err => {
        if (err?.response?.data?.message === "Unauthenticated.") {
          await cookies.remove('verminData');
          await window.location.replace('/');
        }
      })
      //ログイン時のAPI
    } else {
      axios.get(`${process.env.REACT_APP_DEV_API_URL}/search-data/${proc}`, {
        params: {
          word,
          start_date,
          end_date,
          start_time,
          end_time,
          client_code: cookie?.verminData?.client_code,
          time_zone: data.time_zone?.join(''),
        },
        headers: { Authorization: `Bearer ${cookie?.verminData?.api_token}` }
      },
      ).then((data) => {
        setVermins(data.data.results);
        setProc(proc);
        setIds(data.data.ids);
        searchS(watch());
      }).catch(async err => {
        if (err?.response?.data?.message === "Unauthenticated.") {
          await cookies.remove('verminData');
          await window.location.replace('/')
        }
      })
    }
  };

  return (
    <form className={clsx(styles.form, className)} onSubmit={handleSubmit(search)}>
      <button
        type="button"
        className={clsx(styles.chevronButton, isDroped && styles.isActive)}
        aria-label="詳細検索ボックスを開く"
        onClick={dropToggle}
      >
        <IconChevronDown />
      </button>
      <input
        type="search"
        className={styles.input}
        placeholder="例）イノシシ␣目撃␣2021/9/22"
        autoFocus={true}
        {...register('keyword')}
      />
      <button
        type="submit"
        className={styles.searchButton}
        aria-label="検索する"
      >
        <IconSearch />
      </button>
      <div className={clsx(styles.dropbox, isDroped && styles.isActive)} aria-hidden={false}>
        <legend className={styles.caption}>
          検索種別
        </legend>
        <RadiosWrap direction="column">
          <Radio
            value="vermin"
            {...register('proc')}
            defaultChecked={true}>
            害獣
          </Radio>
          <Radio
            value="damage"
            {...register('proc')}>
            被害
          </Radio>
          <Radio
            value="trap"
            {...register('proc')}>
            罠
          </Radio>
          <Radio
            value="camera"
            {...register('proc')}>
            カメラ
          </Radio>
        </RadiosWrap>
        <br />
        <hr />
        <br />
        <fieldset className={styles.fieldset}>
          <legend className={styles.caption}>詳細条件</legend>
          <label className={styles.labelBox}>
            <p className={styles.labelText}>

              開始日
            </p>
            <Controller control={control}
              name='start_day' render={({ field: { onChange, value } }) => (
                <DatePicker onChange={onChange} dateFormat="yyyy/MM/dd"
                  locale={ja} showMonthDropdown={true} showYearDropdown={true}//230419追加
                  className={styles.inputForm} placeholderText='カレンダーから選択' selected={value as unknown as Date} />
              )} />
          </label>
          <label className={styles.labelBox}>
            <p className={styles.labelText}>

              終了日
            </p>
            <Controller control={control}
              name='end_day' render={({ field: { onChange, value } }) => (
                <DatePicker onChange={onChange} dateFormat="yyyy/MM/dd"
                  locale={ja} showMonthDropdown={true} showYearDropdown={true}//230419追加
                  className={styles.inputForm} placeholderText='カレンダーから選択' selected={value as unknown as Date} />
              )} />
          </label>
        </fieldset>
        <br />
        <hr />
        <br />
        { (watch('proc') != "camera" && watch('proc') != "trap") &&
        <fieldset className={styles.fieldset}>
          <ul className={styles.tabBar}>
            <li className={clsx(!activeTab && styles.activeTab)}>
              <button type="button" className={styles.tabButton} onClick={() => setActiveTab(false)}>
                時間帯選択
              </button>
            </li>
            <li className={clsx(activeTab && styles.activeTab)}>
              <button type="button" className={styles.tabButton} onClick={() => setActiveTab(true)}>
                時間帯詳細設定
              </button>
            </li>
          </ul>
          {activeTab &&
            <div className={styles.fieldsRow}>
              <Controller control={control}
                name='start_time' render={({ field: { onChange, value } }) => (
                  <DatePicker onChange={onChange} dateFormat="HH:mm" showTimeSelect showTimeSelectOnly timeCaption="Time"
                    locale={ja}
                    className={styles.inputForm} placeholderText='00:00' selected={value as unknown as Date} />
                )} />
              <Controller control={control}
                name='end_time' render={({ field: { onChange, value } }) => (
                  <DatePicker onChange={onChange} dateFormat="HH:mm" showTimeSelect showTimeSelectOnly timeCaption="Time"
                    locale={ja}
                    className={styles.inputForm} placeholderText='00:00' selected={value as unknown as Date} />
                )} />
            </div>
          }
          {!activeTab &&
            <>
              <div className={styles.checkboxRow2} style={{ display: 'flex' }}>
                <Checkbox2
                  type="checkbox"
                  value="朝:(5:00～9:00)"
                  //label="朝"
                  label="zone"
                  bgImage={picselBox}
                  defaultChecked={true}
                  {...register('time_zone')} />
                <div>&emsp;朝(5:00～9:00)</div>
              </div>
              <div className={styles.checkboxRow2} style={{ display: 'flex' }}>
                <Checkbox2
                  type="checkbox"
                  value="昼:(9:00～15:00)"
                  //label="昼"
                  label="zone"
                  bgImage={picselBox}
                  defaultChecked={true}
                  {...register('time_zone')} />
                <div>&emsp;昼(9:00～15:00)</div>
              </div>
              <div className={styles.checkboxRow2} style={{ display: 'flex' }}>
                <Checkbox2
                  type="checkbox"
                  value="夕方:(15:00～18:00)"
                  //label="夕"
                  label="zone"
                  bgImage={picselBox}
                  defaultChecked={true}
                  {...register('time_zone')} />
                <div>&emsp;夕方(15:00～18:00)</div>
              </div>
              <div className={styles.checkboxRow2} style={{ display: 'flex' }}>
                <Checkbox2
                  type="checkbox"
                  value="夜:(18:00～24:00)"
                  //label="夜"
                  label="zone"
                  bgImage={picselBox}
                  defaultChecked={true}
                  {...register('time_zone')} />
                <div>&emsp;夜(0:00～5:00,18:00～24:00)</div>
              </div>
            </>
          }
          <div className={styles.buttonBox}>
            <Button variant='cancel' onClick={() => {
              reset();
              deleteVermins();
              deleteIds();
              searchE();
            }} className={styles.longButton}>リセット</Button>
            <Button className={styles.longButton} type="submit">検索する</Button>
          </div>
        </fieldset>
        }
        { !(watch('proc') != "camera" && watch('proc') != "trap") &&
        <div className={styles.buttonBox}>
            <Button variant='cancel' onClick={() => {
              reset();
              deleteVermins();
              deleteIds();
              searchE();
            }} className={styles.longButton}>リセット</Button>
            <Button className={styles.longButton} type="submit">検索する</Button>
          </div>
        }
      </div>
    </form >
  );
};

export default React.memo(SearchForm);
